export enum ForecastMethod {
  MOVING_AVERAGE = 'moving_average',
  EXPONENTIAL_SMOOTHING = 'exponential_smoothing',
  LINEAR_REGRESSION = 'linear_regression',
  SEASONAL_NAIVE = 'seasonal_naive',
}

export enum ForecastPeriod {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
}

export interface DemandForecast {
  id: string;
  productId: string;
  productName: string;
  productSku: string;
  method: ForecastMethod;
  period: ForecastPeriod;
  forecastDate: string;
  forecastedDemand: number;
  actualDemand?: number | null;
  accuracy?: number | null;
  meanAbsoluteError?: number | null;
  // Past demand per period, oldest first
  historicalData?: number[] | null;
  seasonalFactors?: number[] | null;
  periodLookback: number;
  smoothingFactor?: number | null;
  isActive: boolean;
  notes?: string | null;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDemandForecastRequest {
  productId: string;
  method: ForecastMethod;
  period: ForecastPeriod;
  forecastDate: string;
  // Left out when historical data is sent: the server calculates it
  forecastedDemand?: number;
  actualDemand?: number | null;
  historicalData?: number[] | null;
  seasonalFactors?: number[] | null;
  periodLookback?: number;
  smoothingFactor?: number;
  isActive?: boolean;
  notes?: string | null;
}

export type UpdateDemandForecastRequest = Partial<CreateDemandForecastRequest>;

export type ActualsFilter = 'recorded' | 'pending';

export interface DemandForecastQueryParams {
  page: number;
  limit: number;
  search?: string;
  method?: ForecastMethod;
  period?: ForecastPeriod;
  isActive?: boolean;
  actuals?: ActualsFilter;
  // Forecast date range, both ends included (YYYY-MM-DD)
  dateFrom?: string;
  dateTo?: string;
}

export interface PaginatedDemandForecasts {
  data: DemandForecast[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DemandForecastSummary {
  total: number;
  active: number;
  withActuals: number;
  // Active forecasts whose date has passed without an actual being recorded
  awaitingActuals: number;
  averageAccuracy: number | null;
  averageError: number | null;
}

export interface CalculateForecastRequest {
  method: ForecastMethod;
  historicalData: number[];
  periodLookback?: number;
  smoothingFactor?: number;
  periods?: number;
  period?: ForecastPeriod;
  startDate?: string;
}

export interface CalculatedForecast {
  forecastDate: string | null;
  forecastedDemand: number;
}

export interface DemandForecastFilters {
  productId?: string;
  method?: ForecastMethod;
  period?: ForecastPeriod;
  isActive?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

export interface GenerateForecastsRequest {
  productId: string;
  historicalSalesData: number[];
  periods?: number;
  method?: ForecastMethod;
  period?: ForecastPeriod;
  startDate?: string;
  periodLookback?: number;
  smoothingFactor?: number;
  isActive?: boolean;
  notes?: string;
}

export interface ForecastAccuracy {
  averageAccuracy: number;
  averageMeanAbsoluteError: number;
  totalForecasts: number;
  forecastsWithActuals: number;
}

export interface UpdateActualDemandRequest {
  actualDemand: number;
}

// Display options for UI components
export const ForecastMethodLabels: Record<ForecastMethod, string> = {
  [ForecastMethod.MOVING_AVERAGE]: 'Moving Average',
  [ForecastMethod.EXPONENTIAL_SMOOTHING]: 'Exponential Smoothing',
  [ForecastMethod.LINEAR_REGRESSION]: 'Linear Regression',
  [ForecastMethod.SEASONAL_NAIVE]: 'Seasonal Naive',
};

export const ForecastPeriodLabels: Record<ForecastPeriod, string> = {
  [ForecastPeriod.DAILY]: 'Daily',
  [ForecastPeriod.WEEKLY]: 'Weekly',
  [ForecastPeriod.MONTHLY]: 'Monthly',
  [ForecastPeriod.QUARTERLY]: 'Quarterly',
};

// Chart data interface for forecast visualization
export interface ForecastChartData {
  date: string;
  forecasted: number;
  actual?: number;
  error?: number;
}

export interface ProductForecastSummary {
  productId: string;
  productName: string;
  productSku: string;
  totalForecasts: number;
  activeForecasts: number;
  averageAccuracy: number;
  lastForecastDate: string;
  nextForecastDate?: string;
}